import { Box, Stack, Theme } from '@mui/material';
import { RachelUIMessage } from '../../../../rachel.types';
import { getRachelPartKey } from '../../../../rachel.utils';
import RachelMessagePart from '../../../RachelMessagePart/RachelMessagePart';
import RachelFilePartView from '../../../RachelMessagePart/components/RachelFilePartView/RachelFilePartView';
import { isRachelFileThumbnail } from '../../../RachelMessagePart/components/RachelFilePartView/rachelFilePartView.utils';
import {
  RACHEL_PROMPT_ACTIONS_SPACING_TOP,
  useRachelMessageActionsStyles,
} from '../../rachelMessageItem.styles';
import RachelCopyMessageButton from '../RachelCopyMessageButton/RachelCopyMessageButton';

type RachelPart = RachelUIMessage['parts'][number];
type RachelFilePart = Extract<RachelPart, { type: 'file' }>;
type RachelTextPart = Extract<RachelPart, { type: 'text' }>;

const TEXT_PARTS_SEPARATOR = '\n\n';

const ATTACHMENTS_GAP = 0.75;

const isFilePart = (part: RachelPart): part is RachelFilePart => part.type === 'file';

const isTextPart = (part: RachelPart): part is RachelTextPart => part.type === 'text';

const isBlankTextPart = (part: RachelPart) => isTextPart(part) && part.text.trim().length === 0;

const isBubblePart = (part: RachelPart) => !isFilePart(part) && !isBlankTextPart(part);

const getBubbleSurfaceSx = (theme: Theme, isOnPage: boolean) => {
  if (isOnPage) {
    return { bgcolor: theme.palette.secondary.light, border: '1px solid transparent' };
  }

  return {
    bgcolor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.custom.border.secondary}`,
  };
};

interface RachelUserMessageProps {
  message: RachelUIMessage;
  isOnPage: boolean;
}

const RachelUserMessage = ({ message, isOnPage }: RachelUserMessageProps) => {
  const { classes } = useRachelMessageActionsStyles({
    shouldRevealOnHover: true,
    spacingTop: RACHEL_PROMPT_ACTIONS_SPACING_TOP,
  });
  const fileParts = message.parts.filter(isFilePart);
  const thumbnailParts = fileParts.filter(isRachelFileThumbnail);
  const chipParts = fileParts.filter((part) => !isRachelFileThumbnail(part));
  const bubbleParts = message.parts.filter(isBubblePart);
  const copyText = message.parts
    .filter(isTextPart)
    .map((part) => part.text)
    .join(TEXT_PARTS_SEPARATOR)
    .trim();

  return (
    <Box className={classes.root} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
      <Stack sx={{ alignItems: 'flex-end', maxWidth: '86%' }}>
        <Stack sx={{ alignItems: 'flex-end', gap: ATTACHMENTS_GAP, maxWidth: '100%' }}>
          {thumbnailParts.length > 0 && (
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'flex-end',
                alignContent: 'flex-start',
                gap: ATTACHMENTS_GAP,
                width: '100%',
              }}
            >
              {thumbnailParts.map((part, index) => (
                <RachelFilePartView key={getRachelPartKey(part, index)} part={part} />
              ))}
            </Box>
          )}
          {chipParts.map((part, index) => (
            <RachelFilePartView key={getRachelPartKey(part, index)} part={part} />
          ))}
          {bubbleParts.length > 0 && (
            <Box
              sx={(theme) => ({
                px: 1.5,
                py: 0.75,
                color: theme.palette.text.primary,
                borderRadius: `${theme.shape.borderRadiusLg}px`,
                ...getBubbleSurfaceSx(theme, isOnPage),
              })}
            >
              <Stack gap={1}>
                {bubbleParts.map((part, index) => (
                  <RachelMessagePart key={getRachelPartKey(part, index)} part={part} />
                ))}
              </Stack>
            </Box>
          )}
        </Stack>
        {!!copyText && (
          <Box className={classes.actions}>
            <RachelCopyMessageButton text={copyText} />
          </Box>
        )}
      </Stack>
    </Box>
  );
};

export default RachelUserMessage;
